import {appApi} from "./api.service";
import {Preferences} from "@capacitor/preferences";
import moment, {Moment} from "moment";
import {SecureStoragePlugin} from "capacitor-secure-storage-plugin";
import {Network} from "@capacitor/network";
import {v4 as uuidv4} from 'uuid';
import * as msgpack from "@msgpack/msgpack";
import { Directory, Encoding, Filesystem } from "@capacitor/filesystem";
import {OFFLINE_REPORT_ID_PREFIX} from "@services/reportOffline.service";

var CryptoJS = require("crypto-js");

let endpointPrefix = 'offline-sync';

export const getSettings = async () => {
    return appApi.get(endpointPrefix + '/settings');
}

const getPlaceOfServices = async () => {
    return appApi.get(endpointPrefix + '/reports-place-of-services');
}

const getReportsData = async (id: string, orderId: string|null = null) => {
    let data = appApi.get(endpointPrefix + '/reports/' + id + (orderId ? '?orderId=' + orderId : ''));

    let settings = await getSettings().then((response) => {
        return response.data;
    })

    await Preferences.set({
        'key': 'offline_sync_duration',
        'value': settings.ep2_offline_sync_duration.toString()
    });

    return data;
}

const getCurrentNetworkStatus = async () => {
    let status = false;
    await Network.getStatus()
        .then(result => {
            status = result.connected;
        });

    return status;
};

const saveDataToOfflineStorage = async (id: string, data: any) => {
    offlineStorageSet('offline_data_' + id, data);

    await Preferences.set({
        'key': 'last_offline_data_update',
        'value': moment().toDate().toString()
    });
}

const getOfflineDataStatus = async() => {
    let now = moment();
    let lastOfflineDataUpdate = await Preferences.get({
        'key': 'last_offline_data_update'
    }).then(value => {
        return value.value
    });
    let offlineSyncDuration = await Preferences.get({
        'key': 'offline_sync_duration'
    }).then(value => {
        return value.value
    });

    let valid;
    if (lastOfflineDataUpdate == null || offlineSyncDuration == null) {
        valid = false;
    } else {
        valid = moment.duration(now.diff(lastOfflineDataUpdate)).asMinutes() < parseInt(offlineSyncDuration);
    }

    return {
        date: lastOfflineDataUpdate,
        valid: valid
    }
}

const CHUNK_SIZE = 1024 * 1024; // 1 MB

function wordArrayToUint8Array(wordArray: any): Uint8Array { // CryptoJS.lib.WordArray
    const words = wordArray.words;
    const sigBytes = wordArray.sigBytes;
    const u8 = new Uint8Array(sigBytes);
    for (let i = 0; i < sigBytes; i++) {
        u8[i] = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
    }
    return u8;
}

function uint8ToWordArray(u8: Uint8Array):any { // CryptoJS.lib.WordArray
    const words: number[] = [];
    for (let i = 0; i < u8.length; i += 4) {
        words.push(
            (u8[i] << 24) |
            (u8[i + 1] << 16) |
            (u8[i + 2] << 8) |
            (u8[i + 3] ?? 0)
        );
    }
    return CryptoJS.lib.WordArray.create(words, u8.length);
}

function splitBuffer(buffer: Uint8Array, size: number): Uint8Array[] {
    const chunks: Uint8Array[] = [];
    for (let i = 0; i < buffer.length; i += size) {
        chunks.push(buffer.slice(i, i + size));
    }
    return chunks;
}

function mergeBuffers(chunks: Uint8Array[]): Uint8Array {
    const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const result = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of chunks) {
        result.set(chunk, offset);
        offset += chunk.length;
    }
    return result;
}

export function encryptLargeData(data: any, key: string): string[] {
    const encoded = msgpack.encode(data);
    const chunks = splitBuffer(encoded, CHUNK_SIZE);

    return chunks.map(chunk => {
        const wordArray = uint8ToWordArray(chunk);
        const encrypted = CryptoJS.AES.encrypt(wordArray, key).toString();
        return encrypted;
    });
}

export function decryptLargeData(chunks: string[], key: string): any {
    const buffers: Uint8Array[] = chunks.map(encrypted => {
        const decrypted = CryptoJS.AES.decrypt(encrypted, key);
        return wordArrayToUint8Array(decrypted);
    });

    const merged = mergeBuffers(buffers);
    return msgpack.decode(merged);
}

const offlineStorageSet = async (key: string, data: any) => {
    const hash = uuidv4();
    const encrypted = encryptLargeData(data, hash);

    await Filesystem.writeFile({
      path: key,
      data: JSON.stringify(encrypted),
      directory: Directory.Data,
      encoding: Encoding.UTF8,
    });

    await SecureStoragePlugin.set({
        key: key,
        value: hash,
    });
};

const offlineStorageGet = async (key: string) => {
    const hash = await SecureStoragePlugin.get({ key: key }).then(res => res.value);
    let data:[] = [];

    try {
        const result = await Filesystem.readFile({
          path: key,
          directory: Directory.Data,
          encoding: Encoding.UTF8
        });

        const encryptedChunks: string[] = JSON.parse(result.data as string);
        data = decryptLargeData(encryptedChunks, hash);
      } catch (err) {
        //
      }

    return data;
};

const offlineStorageRemove = async (key: string) => {
    try {
        await SecureStoragePlugin.remove({key: key})
    } catch (e) {
    }

    try {
        await Filesystem.deleteFile({
            path: key,
            directory: Directory.Data,
        });
    } catch (e) {
    }
}

export {
    getPlaceOfServices,
    getReportsData,
    getCurrentNetworkStatus,
    getOfflineDataStatus,
    offlineStorageSet,
    offlineStorageGet,
    saveDataToOfflineStorage,
    offlineStorageRemove
}



